import { useUser } from "../../../custom-hooks/useUser";
import { IPolicyItem, IPolicyMenuItem } from "./PolicyMenu";
import { ReactComponent as IconPolicy } from "../../../assets/img/icon/menu/policy.svg";
import { ReactComponent as IconAccidentGen } from "../../../assets/img/icon/menu/accident-gen.svg";
import { ReactComponent as IconAccidentVam } from "../../../assets/img/icon/menu/accident-vam.svg";
import { ReactComponent as IconDocMedicalAssistance } from "../../../assets/img/icon/menu/doc-medical-assintance.svg";
import { ReactComponent as IconDocHospitableCredit } from "../../../assets/img/icon/menu/doc-hospitable-credit.svg";
import { ReactComponent as IconCard } from "../../../assets/img/icon/menu/card.svg";
import { ReactComponent as IconVehicleQuotation } from "../../../assets/img/icon/menu/vehicle-quotation.svg";
import { ReactComponent as IconVamQuotation } from "../../../assets/img/icon/menu/vam-quotation.svg";
import { ReactComponent as IconDocument } from "../../../assets/img/icon/menu/document.svg";
import { ReactComponent as IconDeducibles } from "../../../assets/img/icon/menu/deducibles.svg";
import { getUrls } from "../../../util/General";
import { Contractor, EfiModules, Insued, Member } from "../../../interface/user.interface";
import { useCallback } from "react";
import { DeduciblesModal } from "../../../coms/siniestros/DeduciblesModal";
import { routes } from "../../../vam/General";
import { useBoolean } from "../../../custom-hooks/useBoolean";
import { ID_CARD_ITEM, NEWS } from "./menu-items.constants";

export const useMenuItems = () => {
    const {
        isAdmin,
        isBroker,
        isSubAgente,
        isEjecutivo,
        isCotizador,
        isCotizadorVam,
        asociados,
        asociadosVam,
        isCotizadorAdmin,
        isCotizadorVamAdmin,
        ...user
    } = useUser();

    const { value: isOpenMisDeducibles, toggle: toggleMisDeducibles } = useBoolean();

    const hasPrivileges = isAdmin || isBroker;
    const isContractorPrivileged = isAdmin || isEjecutivo || isSubAgente;
    const isInsuredPrivileged = isAdmin || isEjecutivo;
    const showContractorName = asociados.length || !isContractorPrivileged;
    const showInsuredName = asociadosVam.length || !isInsuredPrivileged;

    const genericMember: Member = {
        ...user,
        cdAdicional: user.id,
        usuarioWeb: user.usuario,
        activeCard: true,
    };

    const genericContractor = {
        ...genericMember,
    } as Contractor;

    const genericInsured: Insued = {
        ...genericMember,
        nmCliente: user.nmAsegurado,
        apCliente: user.apAsegurado,
    } as Insued;

    const getContractors = () => {
        if (asociados.length) return asociados;
        if (isContractorPrivileged || isCotizador) return [genericContractor] as Contractor[];
        return [];
    };

    const getInsureds = (contractors: Contractor[]) => {
        if (asociadosVam.length) {
            if (!hasPrivileges && contractors.length)
                return asociadosVam.filter(insured =>
                    contractors.find(contractor => contractor.cedula !== insured.cedula)
                );
            return asociadosVam;
        }
        if (isInsuredPrivileged) return [genericInsured] as Insued[];
        return [];
    };

    const getVehicleQuotationItem = (policy: Contractor | Insued, moduleName: EfiModules | null): IPolicyItem => {
        const uuid = policy.uuid || "";
        const newUuid = moduleName ? uuid : user.uuid;
        const isCorporative = (policy as Contractor)?.tpPersona === 2;
        return {
            title: `Cotizador vehículos${isCotizadorAdmin ? "\n(Admin)" : ""}`,
            icon: IconVehicleQuotation,
            href: `${getUrls().cotizadorUrl}/cotizar?uuid=${newUuid}&module=${moduleName ?? ""}`,
            hrefSamePage: true,
            isDisabled: !isCotizadorAdmin && ((hasPrivileges && !isCotizador) || isCorporative),
        };
    };

    const getVamQuotationItem = (policy: Contractor | Insued, moduleName: EfiModules | null): IPolicyItem => {
        const uuid = policy.uuid || "";
        const newUuid = moduleName ? uuid : user.uuid;
        const isCorporative = (policy as Contractor)?.tpPersona === 2;
        return {
            title: "Cotizador VAM",
            icon: IconVamQuotation,
            href: `${getUrls().cotizadorVamUrl}/cotizar?uuid=${newUuid}&module=${moduleName ?? ""}`,
            hrefSamePage: true,
            isDisabled: !isCotizadorVam || isCotizadorVamAdmin || hasPrivileges || isCorporative,
        };
    };

    const contractors = getContractors();

    const getContractorName = useCallback(() => {
        if (isCotizador) return "";
        return `Contratante${showContractorName ? ":" : ""}`;
    }, [isCotizador, showContractorName]);

    const menuItemsForContractors: IPolicyMenuItem[] = contractors.map(contractor => ({
        module: {
            name: EfiModules.Gen,
            member: contractor,
        },
        name: getContractorName(),
        owner: showContractorName ? `${contractor.nmAsegurado ?? ""} ${contractor.apAsegurado ?? ""}` : "",
        uuid: contractor.uuid,
        items: [
            { title: "Mis pólizas", icon: IconPolicy, to: "/gen/polizas", isDisabled: isCotizador },
            {
                title: "Mis siniestros patrimoniales",
                icon: IconAccidentGen,
                to: "/gen/siniestros-gen",
                isDisabled: isCotizador,
            },
            {
                title: "Mis siniestros médicos",
                icon: IconAccidentVam,
                to: "/gen/siniestros-vam",
                isDisabled: isCotizador,
            },
            {
                title: "Ingreso siniestros médicos",
                icon: IconDocMedicalAssistance,
                to: "/gen/docs-am",
                isDisabled: hasPrivileges || isCotizador,
            },
            {
                title: "Solicitud de crédito hospitalario",
                icon: IconDocHospitableCredit,
                to: "/gen/docs-cred-hosp",
                isDisabled: hasPrivileges || isCotizador,
            },
            {
                id: ID_CARD_ITEM,
                title: "Tarjeta de beneficios",
                icon: IconCard,
                to: "/gen/tarjeta",
                isDisabled: hasPrivileges || !contractor.activeCard || isCotizador,
            },
            getVehicleQuotationItem(contractor, asociados?.length ? EfiModules.Gen : null),
            getVamQuotationItem(contractor, asociados?.length ? EfiModules.Gen : null),
        ],
    }));

    const insureds = getInsureds(contractors);
    const menuItemsForInsureds: IPolicyMenuItem[] = insureds.map(insured => ({
        module: {
            name: EfiModules.Vam,
            member: insured,
        },
        name: `Titular${showInsuredName ? ":" : ""}`,
        owner: showInsuredName ? `${insured.nmCliente ?? ""} ${insured.apCliente ?? ""}` : "",
        uuid: insured.uuid,
        items: [
            { title: "Mis pólizas", icon: IconPolicy, to: "/vam/polizas" },
            { title: "Mis siniestros médicos", icon: IconAccidentVam, to: "/vam/siniestros" },
            {
                title: "Mis deducibles",
                icon: IconDeducibles,
                onClick: toggleMisDeducibles,
                isDisabled: !insured.cedula,
                component: (
                    <DeduciblesModal
                        open={isOpenMisDeducibles}
                        toggleModal={toggleMisDeducibles}
                        cedula={insured.cedula}
                        base={routes.api}
                    />
                ),
            },
            {
                title: "Ingreso siniestros médicos",
                icon: IconDocMedicalAssistance,
                to: "/vam/docs-am",
                isDisabled: hasPrivileges,
            },
            {
                title: "Solicitud de crédito hospitalario",
                icon: IconDocHospitableCredit,
                to: "/vam/docs-cred-hosp",
                isDisabled: hasPrivileges,
            },
            {
                id: ID_CARD_ITEM,
                title: "Tarjeta de beneficios",
                icon: IconCard,
                to: "/vam/tarjeta",
                isDisabled: !insured.activeCard,
            },
            getVehicleQuotationItem(insured, asociadosVam?.length ? EfiModules.Vam : null),
            getVamQuotationItem(insured, asociadosVam?.length ? EfiModules.Vam : null),
        ],
    }));

    const adminMenuItems: IPolicyMenuItem = {
        module: null,
        name: "Administración",
        owner: "",
        uuid: user.uuid,
        items: [
            { title: "Usuarios GEN", icon: IconDocument, to: "/gen/usuarios-gestor" },
            { title: "Usuarios VAM", icon: IconDocument, to: "/vam/usuarios-gestor" },
            { title: "Ejecutivo", icon: IconDocument, to: "/gen/gestor-ejecutivo" },
            { title: "SubAgente", icon: IconDocument, to: "/gen/gestor-subagente" },
            { title: "Visor", icon: IconDocument, to: "/vam/visor" },
            { title: "Beneficios", icon: IconDocument, to: "/gen/beneficios" },
            { title: "Auditoría", icon: IconDocument, to: "/gen/logs" },
            { title: "Mail Zerobounce", icon: IconDocument, to: "/gen/main-zerobounce" },
            { title: "Zoho", icon: IconDocument, to: "/vam/zoho" },
            { title: "Búsqueda", icon: IconDocument, to: "/vam/admin" },
            { title: "TikTok", icon: IconDocument, to: "/vam/tiktok" },
            { title: NEWS, icon: IconDocument, to: "/gen/bannerGen", isDisabled: user.usuario !== "efi" },
        ],
    };

    const modulesMenu: IPolicyMenuItem[] = [...menuItemsForContractors, ...menuItemsForInsureds];
    if (isAdmin) modulesMenu.push(adminMenuItems);

    return modulesMenu;
};
